import compact from 'lodash/compact'
import {
    LaneGroup,
} from 'app/Apps/Pricing/Pricing.types'
import {
    DTOCardFormField,
} from 'app/types/form.types'
import {
    LeaseType,
    LocationType,
    OrderTemperatureRange,
    TransportModeRoad,
    CollectionServiceType,
    DeliveryServiceType,
} from 'app/enums/label.enums'

export type LaneGroupInformationFormFields = Pick<
    LaneGroup,
    | 'laneGroupNumber'
    | 'tempRange'
    | 'leaseType'
    | 'originAirportsIataCode'
    | 'destinationAirportsIataCode'
    | 'deliveryServiceType'
    | 'transportModeRoad'
    | 'isFtlPositioningTemperatureControlled'
    | 'isLtlPositioningTemperatureControlled'
    | 'collectionServiceType'
    | 'collectionDropoffPoint'
    | 'handoverPoint'
> & {
    laneGroup: { id: number }
}

export default ({
    getAllowedValues,
    usePositioningTemperature,
    useTransportModeRoad,
    disabled,
    contractId,
    generateLaneGroupLink,
}: {
    getAllowedValues: (param: Record<string, string>) => Record<string, string>,
    usePositioningTemperature: {
        showFTL: boolean,
        showLTL: boolean,
    },
    useTransportModeRoad: boolean,
    disabled: boolean,
    contractId: number,
    generateLaneGroupLink: (id: number | string) => string,
}) => {
    const handOverTemperatureFields = compact([
        ...(usePositioningTemperature.showFTL ? [{
            name: 'isFtlPositioningTemperatureControlled',
            componentName: 'Checkbox',
            className: 'tlCheckbox',
            labelKey: 'AccountLane.isFtlPositioningTemperatureControlled',
            disabled,
        }] : [null]),
        ...(usePositioningTemperature.showLTL ? [{
            name: 'isLtlPositioningTemperatureControlled',
            componentName: 'Checkbox',
            className: 'tlCheckbox',
            labelKey: 'AccountLane.isLtlPositioningTemperatureControlled',
            disabled,
        }] : [null]),
    ])

    const fields: DTOCardFormField<keyof LaneGroupInformationFormFields>[] = [{
        id: 'laneGroupInformation',
        title: 'Lane Group Information',
        className: 'laneGroupInfo',
        fields: [
            {
                id: 'column1',
                fields: [
                    {
                        name: 'laneGroup',
                        componentName: 'LaneGroupByContractSelector',
                        labelKey: 'LaneFeesView.lgNumber',
                        contractId,
                        noneOption: false,
                        generateLink: generateLaneGroupLink,
                        disabled,
                    },
                    {
                        name: 'tempRange',
                        componentName: 'EnumSelector',
                        allowedValues: getAllowedValues(OrderTemperatureRange),
                        labelKey: 'AccountLane.temperatureRange',
                        disabled: true,
                        required: true,
                    },
                    {
                        name: 'leaseType',
                        componentName: 'EnumSelector',
                        allowedValues: getAllowedValues(LeaseType),
                        labelKey: 'AccountLane.leaseType',
                        disabled: true,
                        required: true,
                    },
                    {
                        name: 'originAirportsIataCode',
                        componentName: 'ListView',
                        labelKey: 'AccountLane.originAirport',
                        filterLocationType: LocationType.AIRPORT,
                        disabled: true,
                    },
                    {
                        name: 'destinationAirportsIataCode',
                        componentName: 'ListView',
                        labelKey: 'AccountLane.destinationAirport',
                        filterLocationType: LocationType.AIRPORT,
                        disabled: true,
                    },
                ],
            },
            {
                id: 'column2',
                fields: [
                    {
                        name: 'deliveryServiceType',
                        componentName: 'EnumSelector',
                        allowedValues: getAllowedValues(DeliveryServiceType),
                        labelKey: 'AccountLane.deliveryServiceType',
                        disabled: true,
                        required: true,
                    },
                    {
                        name: 'transportModeRoad',
                        componentName: 'EnumSelector',
                        allowedValues: getAllowedValues(TransportModeRoad),
                        labelKey: 'AccountLane.transportModeRoad',
                        disabled: true,
                        required: useTransportModeRoad,
                    },
                    ...handOverTemperatureFields,
                ],
            },
            {
                id: 'column3',
                fields: [
                    {
                        name: 'collectionServiceType',
                        componentName: 'EnumSelector',
                        allowedValues: getAllowedValues(CollectionServiceType),
                        labelKey: 'AccountLane.collectionServiceType',
                        disabled: true,
                        required: true,
                    },
                    {
                        name: 'collectionDropoffPoint',
                        componentName: 'LocationObjectSelector',
                        labelKey: 'AccountLane.collectionDropoffPoint',
                        filterLocationType: LocationType.ADDRESS,
                        disabled: true,
                    },
                    {
                        name: 'handoverPoint',
                        componentName: 'LocationObjectSelector',
                        labelKey: 'AccountLane.handoverPoint',
                        filterLocationType: LocationType.ADDRESS,
                        disabled: true,
                    },
                ],
            },
        ],
    }]

    return fields
}
